/**
 * Hardcoded rules that turn a parsed TIDY income estimate into 12 months of
 * projected P&L values. Pure and framework-free (no Angular, no app imports) so
 * it stays isolated and unit-testable.
 *
 * Two kinds of rule live here:
 *   1. Which backend `monthly_finance_type.key` each PDF line maps to.
 *   2. How each annual figure is spread across the calendar year — revenue (and
 *      the %-of-revenue fees) follow a seasonality curve; flat reserves are
 *      split evenly.
 *
 * The seasonality curve is derived from the PDF's own description (ADR peaks
 * ~$301 in July, lows ~$180 in January; occupancy peaks ~91% in March, ~85% in
 * January). The per-month bars on the PDF charts are vector graphics and cannot
 * be read as text, so we model the shape rather than extracting bar values.
 */

import {
  ParsedIncomeEstimate,
  ParsedIncomeEstimateExpense,
} from './income-estimate-pdf-parser';

/** Backend line-item key the gross-revenue figure is written to. */
export const REVENUE_BACKEND_KEY = 'short_term_nightly_revenue';

type Distribution = 'seasonal' | 'even';

interface ExpenseRule {
  /** Backend `monthly_finance_type.key`. */
  backendKey: string;
  /** Seasonal fees scale with revenue; even reserves are 1/12 per month. */
  distribution: Distribution;
}

/**
 * Maps each parsed expense id (see EXPENSE_LABELS in the parser) to a backend
 * line item and how to spread it.
 *
 * Note: the PDF's single "Utilities (host pays)" figure has no generic backend
 * "utilities" line, so it is lumped into `electric` (the utilities
 * subcategory's primary line). The annual total is preserved either way.
 */
export const EXPENSE_RULES: Record<string, ExpenseRule> = {
  airbnb_host_fee:         { backendKey: 'channel_ota_fees',        distribution: 'seasonal' },
  tidy_fee:                { backendKey: 'property_management_fee', distribution: 'seasonal' },
  utilities:               { backendKey: 'electric',                distribution: 'even' },
  consumables:             { backendKey: 'consumables',             distribution: 'even' },
  insurance:               { backendKey: 'property_insurance',      distribution: 'even' },
  maintenance_reserve:     { backendKey: 'general_repairs',         distribution: 'even' },
  // Only present in the LTR-vs-STR comparison layout. Lumped into the closest
  // backend line (like utilities → electric); annual totals are preserved.
  furnishing_depreciation: { backendKey: 'replacement_items',      distribution: 'even' },
  permit_tot:              { backendKey: 'str_permit_fees',        distribution: 'even' },
};

/**
 * Relative monthly weights (Jan…Dec), ADR × occupancy from the PDF's
 * seasonality description. Only the *shape* matters — values are normalized at
 * use, then scaled so the 12 months sum to the PDF's annual figure exactly.
 */
export const STR_MONTHLY_WEIGHTS: readonly number[] = [
  180 * 85, // Jan
  188 * 86, // Feb
  200 * 91, // Mar
  210 * 90, // Apr
  225 * 89, // May
  255 * 90, // Jun
  301 * 91, // Jul (peak)
  295 * 90, // Aug
  250 * 88, // Sep
  215 * 88, // Oct
  192 * 86, // Nov
  205 * 88, // Dec
];

const EVEN_WEIGHTS: readonly number[] = Array.from({ length: 12 }, () => 1);

/**
 * Spread `annualCents` (signed) across 12 months by `weights`, rounding each to
 * whole cents and absorbing the rounding remainder into the last month so the
 * total equals `annualCents` exactly.
 */
export function distributeAnnualToMonths(
  annualCents: number,
  weights: readonly number[] = STR_MONTHLY_WEIGHTS,
): number[] {
  const sum = weights.reduce((a, w) => a + w, 0);
  const out = weights.map((w) => Math.round((annualCents * w) / sum));
  const remainder = annualCents - out.reduce((a, c) => a + c, 0);
  out[out.length - 1] += remainder;
  return out;
}

export interface MonthlyProjectionLine {
  /** Backend `monthly_finance_type.key`. */
  backendKey: string;
  /** 12 signed-cent values indexed by calendar month (0 = Jan … 11 = Dec). */
  monthsByCalendar: number[];
}

/**
 * Build the per-line monthly projection from a parsed income estimate. Revenue
 * and %-of-revenue fees follow the seasonality curve; flat reserves are even.
 * Every line's 12 months sum to its parsed annual figure exactly.
 */
export function buildMonthlyProjection(parsed: ParsedIncomeEstimate): MonthlyProjectionLine[] {
  const lines: MonthlyProjectionLine[] = [
    {
      backendKey: REVENUE_BACKEND_KEY,
      monthsByCalendar: distributeAnnualToMonths(parsed.grossRevenueAnnualCents, STR_MONTHLY_WEIGHTS),
    },
  ];

  for (const expense of parsed.expenses) {
    const rule = EXPENSE_RULES[expense.id];
    if (!rule) {
      continue;
    }
    const weights = rule.distribution === 'seasonal' ? STR_MONTHLY_WEIGHTS : EVEN_WEIGHTS;
    lines.push({
      backendKey: rule.backendKey,
      monthsByCalendar: distributeAnnualToMonths(expense.annualCents, weights),
    });
  }

  return lines;
}

/** Total annual cents the projection nets to (sum of every line, all months). */
export function projectedNetOperatingIncomeCents(parsed: ParsedIncomeEstimate): number {
  const expensesTotal = parsed.expenses.reduce(
    (sum: number, e: ParsedIncomeEstimateExpense) => sum + e.annualCents,
    0,
  );
  return parsed.grossRevenueAnnualCents + expensesTotal;
}

/**
 * Difference (cents) between what we'd project (gross + captured expenses) and
 * the PDF's own stated "Net (annual)". Near zero for a recognized single-column
 * short/mid-term estimate. A large gap means the figures didn't add up — e.g.
 * the PDF is a different layout (an LTR-vs-STR comparison) where we read the
 * wrong column or missed lines, so the parsed numbers shouldn't be trusted.
 * Returns null when the PDF has no stated net to check against.
 */
export function reconciliationGapCents(parsed: ParsedIncomeEstimate): number | null {
  if (parsed.noiAnnualCents === undefined) {
    return null;
  }
  return projectedNetOperatingIncomeCents(parsed) - parsed.noiAnnualCents;
}

/**
 * True when the projection's net matches the PDF's stated net within a small
 * tolerance (the larger of $5 or 1% of gross, to absorb the PDF's own
 * rounding). Unknown (no stated net) counts as reconciled.
 */
export function isReconciled(parsed: ParsedIncomeEstimate): boolean {
  const gap = reconciliationGapCents(parsed);
  if (gap === null) {
    return true;
  }
  const tolerance = Math.max(500, Math.round(parsed.grossRevenueAnnualCents * 0.01));
  return Math.abs(gap) <= tolerance;
}
